import {
  AdminFeeAmountType,
  AdminFee,
  AdminFeeTax,
  Markup,
  MarkupAmountType,
  PriceComponents,
  Tax,
  TaxAmountType,
} from 'application-types';
import {
  getCurrencyPrecision,
  type SupportedPaymentCurrency,
} from 'domains-billing-currency';
import { formatCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';
import BigNumber from 'bignumber.js';

const PERCENTAGE_DIVISOR = 100;

export type PricingParams = {
  currency: SupportedPaymentCurrency;
  locale: SupportedLocaleEnum;
  priceComponents: PriceComponents;
  inclusivePricing?: boolean;
  isAdminFeeFeatureEnabled?: boolean;
};

export type Pricing = {
  rate: number; // Base amount
  grossMarkup: number; // Markup amount in unit currency
  grossTax: number; // Tax amount in unit currency
  grossAdminFee: number; // Admin fee amount in unit currency
  grossAdminFeeTax: number; // Tax on admin fee amount in unit currency
  listPrice: number; // Sum of rate, markup, and admin fee amounts in unit currency
  netPrice: number; // listPrice + grossTax + grossAdminFeeTax in unit currency
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string; // Formatter function that converts the price to a string
};

function isPercentageBased(
  optRate: Markup | Tax | AdminFee | AdminFeeTax
): boolean {
  if ('markupAmountType' in optRate) {
    return (
      optRate.markupAmountType === MarkupAmountType.MarkupAmountTypePercentage
    );
  }

  if ('taxAmountType' in optRate) {
    return optRate.taxAmountType === TaxAmountType.TaxAmountTypePercentage;
  }

  return (
    optRate.adminFeeAmountType ===
    AdminFeeAmountType.AdminFeeAmountTypePercentage
  );
}

export function calculateExclusiveOptionalRate(
  rate: BigNumber,
  optRate: Markup | Tax | AdminFee | AdminFeeTax
) {
  const optValue = 'value' in optRate ? optRate.value : optRate.rate;
  const isPercentage = isPercentageBased(optRate);

  const calculatedValueForPercentage = Number(
    rate.multipliedBy(optValue).dividedBy(PERCENTAGE_DIVISOR).toFixed(2)
  );

  return isPercentage ? calculatedValueForPercentage || 0 : optValue || 0;
}

export function calculateInclusiveOptionalRate(
  price: BigNumber,
  rate: BigNumber
) {
  return price.minus(rate).toNumber() || 0;
}

export function calculateBaseRateFromInclusiveAmount(
  price: BigNumber,
  optRate: Markup | Tax | AdminFee
) {
  const { value } = optRate;
  const isPercentage = isPercentageBased(optRate);

  return isPercentage
    ? price.dividedBy(1 + value / PERCENTAGE_DIVISOR).toNumber() || 0
    : price.minus(value).toNumber() || 0;
}

export function getPricing({
  currency,
  locale,
  priceComponents,
  inclusivePricing = false,
  isAdminFeeFeatureEnabled = false,
}: PricingParams): Pricing {
  const { sellingPrice, amount } = priceComponents;

  const precision = getCurrencyPrecision(currency);

  const formatPrice = (
    amount: number,
    currencyDisplay: 'narrowSymbol' | 'none' = 'none'
  ) =>
    formatCurrency(currency, {
      locale,
      currencyDisplay,
      useGrouping: false,
    })(amount);

  let rate = 0;
  let grossMarkup = 0;
  let grossTax = 0;
  let grossAdminFee = 0;
  let grossAdminFeeTax = 0;
  let listPrice = 0;

  if (
    sellingPrice?.listPrice !== undefined &&
    sellingPrice.grossTax !== undefined &&
    sellingPrice.netPrice !== undefined &&
    sellingPrice.grossMarkup !== undefined
  ) {
    listPrice = Number(formatPrice(sellingPrice.listPrice));
    grossTax = Number(formatPrice(sellingPrice.grossTax));
    rate = Number(formatPrice(amount));
    grossMarkup = Number(formatPrice(sellingPrice.grossMarkup));
    grossAdminFee = Number(formatPrice(sellingPrice.grossAdminFee ?? 0));
    grossAdminFeeTax = Number(formatPrice(sellingPrice.grossAdminFeeTax ?? 0));
  } else if (sellingPrice?.listPrice !== undefined) {
    ({
      listPrice,
      rate,
      grossMarkup,
      grossTax,
      grossAdminFee,
      grossAdminFeeTax,
    } = calculateListPriceOverridePricing(
      priceComponents,
      formatPrice,
      precision,
      isAdminFeeFeatureEnabled
    ));
  } else if (inclusivePricing) {
    if (isAdminFeeFeatureEnabled) {
      ({
        listPrice,
        grossTax,
        rate,
        grossMarkup,
        grossAdminFee,
        grossAdminFeeTax,
      } = calculateInclusivePricingWithRateFees(priceComponents, precision));
    } else {
      ({
        listPrice,
        grossTax,
        rate,
        grossMarkup,
        grossAdminFee,
        grossAdminFeeTax,
      } = calculateInclusivePricing(priceComponents, formatPrice, precision));
    }
  } else if (isAdminFeeFeatureEnabled) {
    ({
      listPrice,
      grossTax,
      rate,
      grossMarkup,
      grossAdminFee,
      grossAdminFeeTax,
    } = calculateExclusivePricingWithRateFees(priceComponents, precision));
  } else {
    ({
      listPrice,
      grossTax,
      rate,
      grossMarkup,
      grossAdminFee,
      grossAdminFeeTax,
    } = calculateExclusivePricing(priceComponents, formatPrice, precision));
  }

  const netPrice = new BigNumber(listPrice)
    .plus(grossTax)
    .plus(grossAdminFeeTax)
    .decimalPlaces(precision)
    .toNumber();

  return {
    rate,
    grossMarkup,
    grossTax,
    grossAdminFee,
    grossAdminFeeTax,
    listPrice,
    netPrice,
    formatPrice,
  };
}

function calculateListPriceOverridePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number,
  _isAdminFeeFeatureEnabled: boolean
) {
  const { sellingPrice, amount } = priceComponents;

  const listPrice = Number(formatPrice(sellingPrice!.listPrice || 0));
  const rate = Number(formatPrice(amount));
  const grossMarkup = Number(formatPrice(sellingPrice!.grossMarkup || 0));
  const grossAdminFee = Number(formatPrice(sellingPrice!.grossAdminFee || 0));
  const grossAdminFeeTax = Number(
    formatPrice(sellingPrice!.grossAdminFeeTax || 0)
  );

  const taxableAmount = new BigNumber(listPrice);

  const grossTax = new BigNumber(
    priceComponents.tax
      ? Number(
          formatPrice(
            calculateExclusiveOptionalRate(taxableAmount, priceComponents.tax)
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    rate,
    grossMarkup,
    grossAdminFee,
    grossAdminFeeTax,
    grossTax,
  };
}

function calculateInclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, netPrice } = priceComponents;

  const listPrice = new BigNumber(
    tax
      ? Number(
          formatPrice(
            calculateBaseRateFromInclusiveAmount(
              new BigNumber(netPrice ?? 0),
              tax
            )
          )
        )
      : (netPrice ?? 0)
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossTax = new BigNumber(
    tax
      ? Number(
          formatPrice(
            calculateInclusiveOptionalRate(
              new BigNumber(netPrice ?? 0),
              new BigNumber(listPrice)
            )
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const rate = new BigNumber(
    markup
      ? Number(
          formatPrice(
            calculateBaseRateFromInclusiveAmount(
              new BigNumber(listPrice),
              markup
            )
          )
        )
      : listPrice
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
          formatPrice(
            calculateInclusiveOptionalRate(
              new BigNumber(listPrice),
              new BigNumber(rate)
            )
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee: 0,
    grossAdminFeeTax: 0,
  };
}

// Types for inclusive pricing helpers

type InclusiveRateConfig = {
  markupRate: number;
  taxRate: number;
  adminFeeRate: number;
  adminFeeTaxRate: number;
  hasMarkup: boolean;
  hasAdminFee: boolean;
  hasTax: boolean;
  hasSeparateAdminFeeTax: boolean;
  markupIsPercentage: boolean;
  adminFeeIsPercentage: boolean;
  adminFeeTaxIsPercentage: boolean;
  taxIsPercentage: boolean;
  fixedMarkup: number;
  fixedAdminFee: number;
  fixedAdminFeeTax: number;
  fixedTax: number;
};

type InclusivePricingResult = {
  rate: number;
  grossAdminFee: number;
  grossAdminFeeTax: number;
  grossTax: number;
  grossMarkup: number;
};

/**
 * Splits fixed tax proportionally between rate+markup and admin fee.
 * Anchors grossAdminFeeTax (proportional share, rounded once), then plugs
 * grossTax = fixedTax − grossAdminFeeTax so the pair sums to fixedTax exactly.
 */
function splitFixedTaxWithGrossTaxPlug(
  fixedTax: number,
  rateAndMarkup: BigNumber,
  adminFee: BigNumber,
  precision: number
): { grossTax: number; grossAdminFeeTax: number } {
  const total = rateAndMarkup.plus(adminFee);

  if (total.isZero()) {
    return { grossTax: 0, grossAdminFeeTax: 0 };
  }

  const grossAdminFeeTax = adminFee
    .dividedBy(total)
    .multipliedBy(fixedTax)
    .decimalPlaces(precision)
    .toNumber();

  const grossTax = new BigNumber(fixedTax)
    .minus(grossAdminFeeTax)
    .decimalPlaces(precision)
    .toNumber();

  return { grossTax, grossAdminFeeTax };
}

function calculateExclusiveOptionalRateRaw(
  rate: BigNumber,
  optRate: Markup | Tax | AdminFee | AdminFeeTax
): BigNumber {
  const optValue = 'value' in optRate ? optRate.value : optRate.rate;
  const isPercentage = isPercentageBased(optRate);

  return isPercentage
    ? rate.multipliedBy(optValue || 0).dividedBy(PERCENTAGE_DIVISOR)
    : new BigNumber(optValue || 0);
}

function isAdminFeeTaxPercentage(
  adminFeeTax: AdminFeeTax | undefined
): boolean {
  return (
    !!adminFeeTax &&
    adminFeeTax.taxAmountType === TaxAmountType.TaxAmountTypePercentage
  );
}

function extractInclusiveRateConfig(
  priceComponents: PriceComponents
): InclusiveRateConfig {
  const { markup, adminFee, adminFeeTax, tax } = priceComponents;

  const taxRate =
    tax && isPercentageBased(tax) ? tax.value / PERCENTAGE_DIVISOR : 0;
  const hasSeparateAdminFeeTax = adminFeeTax && adminFeeTax.rate >= 0;

  let adminFeeTaxRate: number;
  let adminFeeTaxIsPercentage: boolean;
  let fixedAdminFeeTax: number;

  if (hasSeparateAdminFeeTax) {
    adminFeeTaxRate = isAdminFeeTaxPercentage(adminFeeTax)
      ? adminFeeTax!.rate / PERCENTAGE_DIVISOR
      : 0;
    adminFeeTaxIsPercentage = isAdminFeeTaxPercentage(adminFeeTax);
    fixedAdminFeeTax =
      adminFeeTax && !isAdminFeeTaxPercentage(adminFeeTax)
        ? adminFeeTax.rate || 0
        : 0;
  } else {
    adminFeeTaxRate = taxRate;
    adminFeeTaxIsPercentage = !!(tax && isPercentageBased(tax));
    fixedAdminFeeTax = 0;
  }

  const hasAdminFee = !!(adminFee && adminFee.value > 0);

  return {
    markupRate:
      markup && isPercentageBased(markup)
        ? markup.value / PERCENTAGE_DIVISOR
        : 0,
    taxRate,
    adminFeeRate:
      adminFee && isPercentageBased(adminFee)
        ? adminFee.value / PERCENTAGE_DIVISOR
        : 0,
    adminFeeTaxRate,
    hasMarkup: !!(markup && markup.value > 0),
    hasAdminFee,
    hasTax: !!(tax && tax.value > 0),
    hasSeparateAdminFeeTax: !!hasSeparateAdminFeeTax,
    markupIsPercentage: !!(markup && isPercentageBased(markup)),
    adminFeeIsPercentage: !!(adminFee && isPercentageBased(adminFee)),
    adminFeeTaxIsPercentage,
    taxIsPercentage: !!(tax && isPercentageBased(tax)),
    fixedMarkup: markup && !isPercentageBased(markup) ? markup.value || 0 : 0,
    fixedAdminFee:
      adminFee && !isPercentageBased(adminFee) ? adminFee.value || 0 : 0,
    fixedAdminFeeTax,
    fixedTax: tax && !isPercentageBased(tax) ? tax.value || 0 : 0,
  };
}

function calculateInclusiveDivisor(
  config: InclusiveRateConfig,
  withMarkup: boolean
): number {
  const { markupRate, taxRate, adminFeeRate, adminFeeTaxRate } = config;

  if (withMarkup) {
    return (
      (1 + markupRate) * (1 + taxRate) + adminFeeRate * (1 + adminFeeTaxRate)
    );
  }

  return adminFeeRate > 0
    ? 1 + taxRate + adminFeeRate * (1 + adminFeeTaxRate)
    : 1 + taxRate;
}

function calculateInclusiveWithMarkupPlug(
  netPriceValue: BigNumber,
  config: InclusiveRateConfig,
  precision: number
): InclusivePricingResult {
  const {
    markupRate,
    taxRate,
    adminFeeRate,
    adminFeeTaxRate,
    hasTax,
    taxIsPercentage,
    fixedTax,
    hasAdminFee,
    adminFeeIsPercentage,
    fixedAdminFee,
    adminFeeTaxIsPercentage,
    fixedAdminFeeTax,
    hasSeparateAdminFeeTax,
  } = config;

  const divisor = calculateInclusiveDivisor(config, true);

  // Fixed amounts are not captured in the divisor and must be subtracted
  // before dividing to find the base rate.
  let amountForCalculation = netPriceValue;

  if (!adminFeeIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedAdminFee);
  }

  if (hasAdminFee && !adminFeeTaxIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedAdminFeeTax);
  }

  if (!taxIsPercentage) {
    amountForCalculation = amountForCalculation.minus(fixedTax);
  }

  // Raw layer — full-precision BigNumber, no rounding
  const rateRaw = amountForCalculation.dividedBy(divisor);

  let grossAdminFeeRaw: BigNumber;

  if (!hasAdminFee) {
    grossAdminFeeRaw = new BigNumber(0);
  } else if (adminFeeIsPercentage) {
    grossAdminFeeRaw = rateRaw.multipliedBy(adminFeeRate);
  } else {
    grossAdminFeeRaw = new BigNumber(fixedAdminFee);
  }

  let grossAdminFeeTaxRaw: BigNumber;

  if (!hasAdminFee) {
    grossAdminFeeTaxRaw = new BigNumber(0);
  } else if (adminFeeTaxIsPercentage) {
    grossAdminFeeTaxRaw = grossAdminFeeRaw.multipliedBy(adminFeeTaxRate);
  } else {
    grossAdminFeeTaxRaw = new BigNumber(fixedAdminFeeTax);
  }

  const markupEstimatedRaw = rateRaw.multipliedBy(markupRate);

  let grossTaxRaw: BigNumber;

  if (!hasTax) {
    grossTaxRaw = new BigNumber(0);
  } else if (taxIsPercentage) {
    grossTaxRaw = rateRaw.plus(markupEstimatedRaw).multipliedBy(taxRate);
  } else {
    grossTaxRaw = new BigNumber(fixedTax);
  }

  // Anchor layer — round each independent component exactly once
  const rate = rateRaw.decimalPlaces(precision).toNumber();
  const grossAdminFee = grossAdminFeeRaw.decimalPlaces(precision).toNumber();
  let grossAdminFeeTax = grossAdminFeeTaxRaw
    .decimalPlaces(precision)
    .toNumber();
  let grossTax = grossTaxRaw.decimalPlaces(precision).toNumber();

  const shouldSplitFixedTax =
    !taxIsPercentage && fixedTax > 0 && !hasSeparateAdminFeeTax;

  if (shouldSplitFixedTax) {
    const split = splitFixedTaxWithGrossTaxPlug(
      fixedTax,
      new BigNumber(rate).plus(markupEstimatedRaw.decimalPlaces(precision)),
      new BigNumber(grossAdminFee),
      precision
    );

    grossTax = split.grossTax;
    grossAdminFeeTax = split.grossAdminFeeTax;
  }

  // Plug layer — markup absorbs all remainder, sum invariant holds exactly
  const grossMarkup = netPriceValue
    .minus(rate)
    .minus(grossAdminFee)
    .minus(grossAdminFeeTax)
    .minus(grossTax)
    .decimalPlaces(precision)
    .toNumber();

  return {
    rate,
    grossAdminFee,
    grossAdminFeeTax,
    grossTax,
    grossMarkup,
  };
}

function calculateInclusiveWithAdminFeePlug(
  netPriceValue: BigNumber,
  config: InclusiveRateConfig,
  precision: number
): InclusivePricingResult {
  const {
    taxRate,
    adminFeeTaxRate,
    hasTax,
    taxIsPercentage,
    fixedTax,
    hasMarkup,
    hasAdminFee,
    fixedMarkup,
    fixedAdminFee,
    adminFeeTaxIsPercentage,
    fixedAdminFeeTax,
    hasSeparateAdminFeeTax,
  } = config;

  const divisor = calculateInclusiveDivisor(config, false);

  // Raw layer — strip fixed parts so percentage divisor isolates the base rate.
  const taxOnFixedMarkupRaw = taxIsPercentage
    ? new BigNumber(fixedMarkup).multipliedBy(taxRate)
    : new BigNumber(0);

  let baseBeforeFixedFeesRaw = netPriceValue
    .minus(fixedMarkup)
    .minus(fixedAdminFee)
    .minus(fixedTax)
    .minus(taxOnFixedMarkupRaw);

  if (hasAdminFee && !adminFeeTaxIsPercentage) {
    baseBeforeFixedFeesRaw = baseBeforeFixedFeesRaw.minus(fixedAdminFeeTax);
  }

  const rateRaw = baseBeforeFixedFeesRaw.dividedBy(divisor);
  const grossMarkupRaw = hasMarkup
    ? new BigNumber(fixedMarkup)
    : new BigNumber(0);

  // Anchor rate and markup once
  const rate = rateRaw.decimalPlaces(precision).toNumber();
  const grossMarkup = grossMarkupRaw.decimalPlaces(precision).toNumber();

  // Raw grossTax derived from raw rate (not the rounded anchor) to preserve precision.
  // Special case: no admin fee + percentage tax — tax is the complement of (rate + markup)
  // against netPrice, so derive it from rounded anchors to match historic behavior.
  let grossTax: number;

  if (hasTax && taxIsPercentage && !hasAdminFee) {
    grossTax = netPriceValue
      .minus(rate)
      .minus(grossMarkup)
      .decimalPlaces(precision)
      .toNumber();
  } else if (hasTax && taxIsPercentage) {
    grossTax = rateRaw
      .plus(fixedMarkup)
      .multipliedBy(taxRate)
      .decimalPlaces(precision)
      .toNumber();
  } else if (hasTax) {
    grossTax = new BigNumber(fixedTax).decimalPlaces(precision).toNumber();
  } else {
    grossTax = 0;
  }

  // Plug layer — admin fee + admin fee tax absorb the remainder
  const adminFeeWithAdminFeeTaxRaw = netPriceValue
    .minus(rate)
    .minus(grossTax)
    .minus(grossMarkup);

  let grossAdminFee: number;
  let grossAdminFeeTax: number;

  const shouldSplitFixedTax =
    !taxIsPercentage && fixedTax > 0 && !hasSeparateAdminFeeTax;

  if (shouldSplitFixedTax) {
    grossAdminFee = adminFeeWithAdminFeeTaxRaw
      .decimalPlaces(precision)
      .toNumber();

    const split = splitFixedTaxWithGrossTaxPlug(
      fixedTax,
      new BigNumber(rate).plus(grossMarkup),
      new BigNumber(grossAdminFee),
      precision
    );

    grossTax = split.grossTax;
    grossAdminFeeTax = split.grossAdminFeeTax;
  } else if (hasAdminFee && adminFeeTaxIsPercentage) {
    // Split adminFeeWithAdminFeeTax into adminFee (anchor) and adminFeeTax (plug)
    grossAdminFee = adminFeeWithAdminFeeTaxRaw
      .dividedBy(1 + adminFeeTaxRate)
      .decimalPlaces(precision)
      .toNumber();
    grossAdminFeeTax = adminFeeWithAdminFeeTaxRaw
      .minus(grossAdminFee)
      .decimalPlaces(precision)
      .toNumber();
  } else if (hasAdminFee) {
    grossAdminFeeTax = new BigNumber(fixedAdminFeeTax)
      .decimalPlaces(precision)
      .toNumber();
    grossAdminFee = adminFeeWithAdminFeeTaxRaw
      .minus(grossAdminFeeTax)
      .decimalPlaces(precision)
      .toNumber();
  } else {
    grossAdminFee = 0;
    grossAdminFeeTax = 0;
  }

  return { rate, grossAdminFee, grossAdminFeeTax, grossTax, grossMarkup };
}

function calculateInclusivePricingWithRateFees(
  priceComponents: PriceComponents,
  precision: number
) {
  const netPriceValue = new BigNumber(priceComponents.netPrice ?? 0);

  if (netPriceValue.isZero() || netPriceValue.isNaN()) {
    return {
      listPrice: 0,
      grossTax: 0,
      rate: 0,
      grossMarkup: 0,
      grossAdminFee: 0,
      grossAdminFeeTax: 0,
    };
  }

  const config = extractInclusiveRateConfig(priceComponents);

  const { rate, grossAdminFee, grossAdminFeeTax, grossTax, grossMarkup } =
    config.hasMarkup && config.markupIsPercentage
      ? calculateInclusiveWithMarkupPlug(netPriceValue, config, precision)
      : calculateInclusiveWithAdminFeePlug(netPriceValue, config, precision);

  const listPrice = new BigNumber(rate)
    .plus(grossMarkup)
    .plus(grossAdminFee)
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee,
    grossAdminFeeTax,
  };
}

function calculateExclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, amount } = priceComponents;

  const rate = new BigNumber(amount || 0).decimalPlaces(precision).toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
          formatPrice(
            calculateExclusiveOptionalRate(new BigNumber(amount), markup)
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const listPrice =
    new BigNumber(amount)
      .plus(grossMarkup)
      .decimalPlaces(precision)
      .toNumber() || 0;

  const grossTax = new BigNumber(
    tax
      ? Number(
          formatPrice(
            calculateExclusiveOptionalRate(new BigNumber(listPrice), tax)
          )
        )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee: 0,
    grossAdminFeeTax: 0,
  };
}

function calculateExclusivePricingWithRateFees(
  priceComponents: PriceComponents,
  precision: number
) {
  const { markup, adminFee, tax, amount, adminFeeTax } = priceComponents;
  const amountRaw = new BigNumber(amount || 0);

  // Raw layer — derive each component at full precision from raw inputs
  const grossMarkupRaw = markup
    ? calculateExclusiveOptionalRateRaw(amountRaw, markup)
    : new BigNumber(0);

  const grossAdminFeeRaw = adminFee
    ? calculateExclusiveOptionalRateRaw(amountRaw, adminFee)
    : new BigNumber(0);

  const hasSeparateAdminFeeTax = adminFeeTax && adminFeeTax.rate >= 0;
  const taxIsFixed = tax && !isPercentageBased(tax);
  const shouldSplitFixedTax = taxIsFixed && !hasSeparateAdminFeeTax;

  // Admin fee tax — computed from raw admin fee, not the rounded anchor
  let grossAdminFeeTaxRaw = new BigNumber(0);

  if (!grossAdminFeeRaw.isZero() && !shouldSplitFixedTax) {
    if (hasSeparateAdminFeeTax) {
      grossAdminFeeTaxRaw = calculateExclusiveOptionalRateRaw(
        grossAdminFeeRaw,
        adminFeeTax!
      );
    } else if (tax) {
      grossAdminFeeTaxRaw = calculateExclusiveOptionalRateRaw(
        grossAdminFeeRaw,
        tax
      );
    }
  }

  // Sales tax — computed from raw (amount + markup), preserving precision
  const grossTaxRaw = tax
    ? calculateExclusiveOptionalRateRaw(amountRaw.plus(grossMarkupRaw), tax)
    : new BigNumber(0);

  // Anchor layer — each independent component rounded exactly once
  const rate = amountRaw.decimalPlaces(precision).toNumber();
  const grossMarkup = grossMarkupRaw.decimalPlaces(precision).toNumber();
  const grossAdminFee = grossAdminFeeRaw.decimalPlaces(precision).toNumber();
  let grossAdminFeeTax = grossAdminFeeTaxRaw
    .decimalPlaces(precision)
    .toNumber();
  let grossTax = grossTaxRaw.decimalPlaces(precision).toNumber();

  // listPrice is a derived sum of already-rounded anchors (single rounding)
  const listPrice =
    amountRaw
      .plus(grossMarkup)
      .plus(grossAdminFee)
      .decimalPlaces(precision)
      .toNumber() || 0;

  if (shouldSplitFixedTax && tax) {
    const split = splitFixedTaxWithGrossTaxPlug(
      tax.value,
      new BigNumber(rate).plus(grossMarkup),
      new BigNumber(grossAdminFee),
      precision
    );

    grossTax = split.grossTax;
    grossAdminFeeTax = split.grossAdminFeeTax;
  }

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
    grossAdminFee,
    grossAdminFeeTax,
  };
}
